import { ATTR_PATH_HINTS } from "csi/model/customView";
import csiPrefs from "csi/prefs";
import { PreferenceView } from "csi/prefs/PreferenceView";
import { C8AttributeKey, C8NodeType, cnl } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { cloneDeep, each, forEach, invert, isUndefined, omit, omitBy, transform } from "lodash";
import { CUSTOM_VIEW_VERSION, ICustomViewAttribute, ICustomViewFilter, ICustomViewFilterCondition, ICustomViewGroup, ICustomViewReport, ICustomViewSort } from "src/c8-platform/type/customView";
import { getCustomViewFilterGroup } from "./customViewDataManagementService";
import { CustomViewAttributes } from "./customViewService";
import { ICustomViewChartInMemory, ICustomViewDataSheetOptions, ICustomViewGalleryOptions, ICustomViewLocalStorageOptions, ICustomViewOptions, ICustomViewOptionsInLocalStorage, ICustomViewQueryOptions, ICustomViewTableOptions, IViewSettingOptions } from "./options/CustomViewOptions";
import ChangeNode from "csi/xml/ChangeNode";

type MapKeyFunction = (key: C8AttributeKey) => string;
type SaveSettingFunction = (options: ICustomViewQueryOptions, f: MapKeyFunction) => any;

export const CustomViewOptionSaveFunctions = {};

const registerCustomViewSaveOptionFunctions = (optionName: string, f: SaveSettingFunction): void => {
	CustomViewOptionSaveFunctions[optionName] = f;
};

registerCustomViewSaveOptionFunctions("attributeKeys", attributeKeysSave);
registerCustomViewSaveOptionFunctions("chartSettings", chartSettingsSave);
registerCustomViewSaveOptionFunctions("filterSettings", filterSettingsSave);
registerCustomViewSaveOptionFunctions("fixedColumnWidths", fixedColumnWidthsSave);
registerCustomViewSaveOptionFunctions("groupSettings", groupSettingsSave);
registerCustomViewSaveOptionFunctions("hiddenKeys", hiddenKeysSave);
registerCustomViewSaveOptionFunctions("namesSettings", namesSettingsSave);
registerCustomViewSaveOptionFunctions("reportSettings", reportSettingsSave);
registerCustomViewSaveOptionFunctions("sortSettings", sortSettingsSave);

/** substitute indices for typed keys in attributes value */
function mapAttributeKeys(keys: ICustomViewAttribute[], mapKey: MapKeyFunction): ICustomViewAttribute[] {
	return keys.map((groupOrKey) => {
		if (typeof groupOrKey === "string") {
			return mapKey(groupOrKey);
		} else {
			const { groupKey, keys, matrixTarget, name } = groupOrKey;
			const mappedKeys = keys.map(mapKey);
			const params = omitBy({
				groupKey: mapKey(groupKey) || undefined,
				matrixTarget,
				name,
			}, isUndefined);

			return {
				keys: mappedKeys,
				...params,
			};
		}
	});
};

/** save filter settings  */
export function filterSettingsSave(options: ICustomViewQueryOptions, mapKey: MapKeyFunction): Record<string, ICustomViewFilter> {
	const filterSettings = cloneDeep(options.filterSettings);

	forEach(filterSettings, (setting) => {
		setting.C.forEach((condition) => {
			const group = getCustomViewFilterGroup(condition);
			if (group) {
				group.conditions.forEach((filterGroupCondition) => {
					if (filterGroupCondition.key = mapKey(filterGroupCondition.key)) {
						if (filterGroupCondition.relative) {
							filterGroupCondition.value = mapKey(filterGroupCondition.value as C8AttributeKey);
						}
					}
				});
			} else {
				const filter = condition as ICustomViewFilterCondition;
				if (filter.key = mapKey(filter.key)) {
					if (filter.relative) {
						filter.value = mapKey(filter.value as C8AttributeKey);
					}
				}
			}
		});
	});

	return filterSettings;
};

/**
 * Map keys in object storing column widths
 */
export function fixedColumnWidthsSave(options: ICustomViewTableOptions, mapKey: MapKeyFunction): Record<C8AttributeKey, number> {
	return transform(options.fixedColumnWidths, (result, value, key) => {
		result[mapKey(key)] = value;
	}, {});
}

export function chartSettingsSave(options: ICustomViewTableOptions, mapKey: MapKeyFunction): ICustomViewChartInMemory[] {
	const chartSettings = options.chartSettings;

	return chartSettings.map((chart) => ({
		N: chart.chartName,
		S: chart.chartSubName,
		T: chart.chartTemplate,
		GX: mapKey(chart.chartGroupX),
		GY: mapKey(chart.chartGroupY),
		V1: mapKey(chart.chartValue),
		V2: mapKey(chart.chartValue2),
		X: chart.chartXAxisName,
		Y: chart.chartYAxisName,
		W: chart.chartWidth,
		H: chart.chartHeight,
		SL: chart.chartShowLabels,
		SV: chart.chartShowValues,
		SP: chart.chartShowPercentageValues,
		SB: chart.chartSortBy,
		DP: chart.chartPercentageDecimalPlaces,
	}));
};

export function groupSettingsSave(options: ICustomViewQueryOptions, mapKey: MapKeyFunction): ICustomViewGroup {
	const groupSettings = options.groupSettings;
	return {
		key1: mapKey(groupSettings.key1),
		key2: mapKey(groupSettings.key2),
		expand1: groupSettings.expand1,
		expand2: groupSettings.expand2,
	};
};

export function namesSettingsSave(options: ICustomViewQueryOptions, mapKey: MapKeyFunction): Record<string, string> {
	return transform(options.namesSettings, (result, value, key) => {
		result[mapKey(key)] = value;
	}, {});
};

export function sortSettingsSave(options: ICustomViewQueryOptions, mapKey: MapKeyFunction): ICustomViewSort[] {
	const sortSettings = options.sortSettings;
	return sortSettings.map((sortSetting) => ({
		key: mapKey(sortSetting.key),
		descending: sortSetting.descending,
	} as ICustomViewSort));
};

export function reportSettingsSave(options: ICustomViewQueryOptions, mapKey: MapKeyFunction): ICustomViewReport[] {
	const reportSettings = options.reportSettings;

	return reportSettings.map((reportSetting) => {
		const { func, key, key2: settingkey2 } = reportSetting;
		const result = {
			func,
			key: mapKey(key),
			key2: mapKey(settingkey2),
		};

		const key2 = mapKey(settingkey2);
		if (key2) {
			result.key2 = key2;
		}

		return result;
	});
};

/** substitute indices into key map for keys in attributeKeys option */
export function attributeKeysSave(options: ICustomViewOptions, mapKey: MapKeyFunction): ICustomViewAttribute[] {
	return mapAttributeKeys(options.attributeKeys, mapKey);
};

/** substitute indices into key map for keys in hiddenKeys option */
export function hiddenKeysSave(options: ICustomViewOptions, mapKey: MapKeyFunction): ICustomViewAttribute[] {
	return mapAttributeKeys(options.hiddenKeys, mapKey);
};

/** convert the grouped structure of attribute keys to a flat list of attributes as was persisted in version 3 and before custom views */
export function convertAttributeKeysToAttributes(attributeKeys: ICustomViewAttribute[]): string[] {
	const attributes: string[] = [];

	attributeKeys?.forEach((groupOrIndex) => {
		if (typeof groupOrIndex === "string") {
			attributes.push(groupOrIndex);
		} else {
			const { groupKey, keys, matrixTarget } = groupOrIndex;

			if (matrixTarget) {
				attributes.push(groupKey);
			}

			attributes.push(...keys);
		}
	});

	return attributes;
}

/** substitue some data in option values with numeric keys and construct the key map for it */
export function convertToPersistedValues(
	optionValues: ICustomViewOptions,
	persistedValues: ICustomViewOptions,
	existingKeyMap?: Record<string, C8AttributeKey>,
): Record<number, C8AttributeKey> {
	const attrMap: Record<C8AttributeKey, string> = existingKeyMap ? invert(existingKeyMap) : {};
	const keyMap: Record<string, C8AttributeKey> = existingKeyMap || {};
	let keyCounter = 0;

	const mapKey = (typedKey: C8AttributeKey): string => {
		if (!typedKey) {
			return "";
		}

		let indexKey = attrMap[typedKey];
		if (!indexKey) {
			indexKey = String(++keyCounter);
			attrMap[typedKey] = indexKey;
			keyMap[indexKey] = typedKey;
		}

		return indexKey;
	};

	// update counter to max index in map so any subsequent keys have a new index
	Object.keys(keyMap).forEach((key) => {
		keyCounter = Math.max(keyCounter, Number(key));
	});

	each(optionValues, (value, key) => {
		if (value != null) {
			const saveFunction = CustomViewOptionSaveFunctions[key];
			if (saveFunction) {
				value = saveFunction(optionValues, mapKey);
			}
		}

		persistedValues[key] = value;
	});

	return keyMap;
};

/** return local storage specific options given all custom view options */
export function getCustomViewLocalStorageOptions(options: ICustomViewOptionsInLocalStorage): ICustomViewLocalStorageOptions {
	const { CV, KEYS, TS, VR } = options;
	return { CV, KEYS, TS, VR };
}

/**  filter out the empty group, if the group is empty for regular view, remove the group, while we keep the empty group for personalized view */
export function filterOutEmptyGroup(groupOrKey: ICustomViewAttribute): boolean {
	if (typeof groupOrKey === "string") {
		return true;
	} else {
		const { keys } = groupOrKey;
		return keys.length > 0;
	}
}

/** save custom view node with the options */
export function writeCustomView(
	writer: Writer,
	customViewContainerUrl: cnl,
	customViewPathHints: string,
	optionValues: ICustomViewOptions,
	selectedCustomViewCnl: cnl,
	targetType: C8NodeType,
	sourceViewCnl: cnl,
	selectedCnlIsNew?: boolean,
): ChangeNode {
	const { prefDescription, prefIsShared, prefName, prefParty } = optionValues as IViewSettingOptions;
	const forceNewUrl = !xml.isValidURL(selectedCustomViewCnl) || selectedCnlIsNew;
	let options: ICustomViewOptions = {};
	const keyMap = convertToPersistedValues(optionValues, options);

	if (!sourceViewCnl && options.attributeKeys) {
		// throw out the empty group for the regular view
		options.attributeKeys = options.attributeKeys.filter(filterOutEmptyGroup);
	}

	const querySize = (options as ICustomViewQueryOptions).querySize;
	const nestedViews = (options as ICustomViewDataSheetOptions).nestedViews;
	const DONT_PERSIST_OPTIONS = [
		"nestedViews",
		"querySize",
		"prefName",
		"prefParty",
		"prefDescription",
		"prefIsShared",
	];

	options = omit(options, DONT_PERSIST_OPTIONS);
	options["VR"] = CUSTOM_VIEW_VERSION;

	// populate Attributes as previously in order to (mostly) support clients reading old views;
	// they cannot accurately represent attribute groups, but other than that can allow for clients to gradually
	// update to new grouped structure of attributeKeys
	const attributes = convertAttributeKeysToAttributes(options.attributeKeys);

	if (customViewContainerUrl) {
		// write operations to persist changes
		writer.createNode(customViewContainerUrl, csiPrefs.TYPE_CONTAINER, null, null, true)
			.changeAttribute(ATTR_PATH_HINTS, xml.ATTR_STRING, customViewPathHints || "");
	}

	const cn = writer.createNode(selectedCustomViewCnl, csiPrefs.TYPE_VIEW, null, null, true)
		.changeAttribute(CustomViewAttributes.OPTIONS, xml.ATTR_STRING, JSON.stringify(options))
		.changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, prefName)
		.changeAttribute(CustomViewAttributes.KEYS, xml.ATTR_STRINGMAP, keyMap)
		.changeAttribute(CustomViewAttributes.ATTRIBUTES, xml.ATTR_STRINGVECTOR, attributes)
		.changeAttribute(CustomViewAttributes.DESCRIPTION, xml.ATTR_STRING, prefDescription)
		.changeAttribute(CustomViewAttributes.IS_SHARED, xml.ATTR_BOOLEAN, prefIsShared)
		.changeAttribute(csiPrefs.ATTR_UMENTRY, xml.ATTR_REFLIST, prefParty)
		.changeAttribute(CustomViewAttributes.NESTED_VIews, xml.ATTR_REFMAP, nestedViews || {})
		.changeAttribute(CustomViewAttributes.PAGE_SIZE, xml.ATTR_INTEGER, querySize)
		.changeAttribute(CustomViewAttributes.SOURCE_VIEW, xml.ATTR_REF, sourceViewCnl)
		.changeAttribute(CustomViewAttributes.TARGET_CLASS, xml.ATTR_STRING, targetType);

	if (customViewContainerUrl && forceNewUrl) {
		cn.attach(customViewContainerUrl, csiPrefs.ATTR_PREFERENCES);
	}

	return cn;
}

/** remove site preferences and role preferences if the view is no longer shared */
export function writeRemovedCustomViewDefaults(writer: Writer, customView: PreferenceView): void {
	const url = customView.urlNode;
	const party = customView.getParty();
	let q;

	q = writer.createQuery();
	q.Type("UserPreferences");
	q.RefAttr("DefaultViews", xml.OP_EQUALS, url);
	q.Not().RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, party);
	writer.changeQuery(q).changeAttribute("DefaultViews", xml.ATTR_REFLIST, [url], false, true);

	q = writer.createQuery();
	q.Type("SitePreferences");
	q.RefAttr("SiteDefaultViews", xml.OP_EQUALS, url);
	q.Not().RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, party);
	writer.changeQuery(q).changeAttribute("SiteDefaultViews", xml.ATTR_REFLIST, [url], false, true);
};
